import { Component, OnInit } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { IClassifier } from '../../models/Classifier';
import { AppService } from '../../services/app.service';
import { ClassifierService } from '../../services/classifier.service';
import { ParameterService } from '../../services/parameter.service';
import { ITableColumn } from '../../shared/table/table.component';
import { EmployeeApplicationService } from '../../services/employee-application.service';
import { IEmployeeStructure, IEmployeeApplication } from '../../models/EmployeeApplication';
import { debounceTime } from 'rxjs/operators';
import { Utils } from '../../core/Utils';

/**
 * Employee application list page
 */
@Component({
    selector: 'app-my-employee-applications',
    templateUrl: './my-applications.component.html',
    styleUrls: ['./application.component.scss']
})
export class MyEmployeeApplicationsComponent implements OnInit {
    constructor(
        public app: AppService,
        private service: EmployeeApplicationService,
        private classifiers: ClassifierService,
        private parameters: ParameterService
    ) { }

    items: IEmployeeApplication[] = [];
    structures: IEmployeeStructure[] = [];

    page: number = 1;
    pageSize: number = 10;
    total: number = 0;
    sortBy: string;
    sortDir: 'asc' | 'desc';

    isAdmin: boolean;

    readonly filters: {
        structureId: string[],
        from?: Date,
        to?: Date,
        search?: string
    } = {
            structureId: []
        };

    readonly structureFilterFn = (option: IEmployeeStructure, term: string) => option.Name.toLowerCase().includes(term.toLowerCase());

    readonly columns: ITableColumn[] = [
        { property: 'Type', label: 'myEmployeeApplications_lblApplicationName', sorts: true },
        { property: 'FullName', label: 'myEmployeeApplications_lblEmployee', sorts: true, cssClass: 'hidden-sm-down' },
        { property: 'Structure', label: 'myEmployeeApplications_lblStructure', sorts: true, cssClass: 'hidden-md-down' },
        { property: 'Created', label: 'myEmployeeApplications_lblSubmitted', sorts: true, type: 'date', cssClass: 'hidden-md-down' },
        { property: 'Registered', label: 'myEmployeeApplications_lblRegistered', sorts: true, type: 'date', cssClass: 'hidden-md-down' },
        { property: 'Status', label: 'myEmployeeApplications_lblStatus', sorts: true, cssClass: 'hidden-sm-down' },
        //{ width: '1px' }
    ];

    private types: IClassifier[] = [];
    private filterSubj = new Subject();

    ngOnInit() {
        this.isAdmin = this.app.currentUser.hasRight('EMPLOYEE_APPLICATION.SET_EMPLOYEE');

        this.filterSubj.pipe(debounceTime(300)).subscribe(() => {
            this.fetch(1);
        });

        if (this.isAdmin) {
            this.service.getStructures().subscribe(data => {
                this.structures = data;
            });
        }

        this.app.addLoading(forkJoin([
            this.classifiers.get('EmployeeApplicationType'),
            this.parameters.getValues()
        ])).subscribe(([types, para]) => {
            this.types = types;

            let pageSizeItem = para.find(t => t.Code === 'EmployeeApplicationHistoryPageSize');

            if (pageSizeItem) {
                this.pageSize = parseInt(pageSizeItem.Value) || 10;
            }

            this.fetch();
        });
    }

    onPage(event: number) {
        this.page = event;
        this.fetch();
    }

    onSort(event: { sortBy: string, sortDir: 'asc' | 'desc' }) {
        this.sortBy = event.sortBy;
        this.sortDir = event.sortDir;
        this.fetch();
    }

    filter() {
        this.filterSubj.next();
    }

    getTypeDisplayName(applicationType: string): string {
        const item = this.types.find(t => t.Code === `EmployeeApplicationType${applicationType}`);
        return item?.Value || applicationType;
    }

    download(row: IEmployeeApplication) {
        this.app.addLoading(this.service.download(row.Id)).subscribe();
    }

    private fetch(page?: number) {
        const pp = page || this.page;

        this.app.addLoading(this.service.get({
            page: pp,
            size: this.pageSize,
            dir: this.sortDir,
            sort: this.sortBy
        }, {
            from: this.filters.from ? Utils.getInputDateString(this.filters.from) : undefined,
            to: this.filters.from ? Utils.getInputDateString(this.filters.to) : undefined,
            search: this.filters.search,
            structureId: this.filters.structureId
        })).subscribe(data => {
            this.items = data.items;
            this.total = data.total;
            this.page = pp;
        });
    }
}
